import { useEffect, useRef, useState } from 'react';
import s from '@/components/ui/Navbar/Navbar.module.css';
import { Ellipsis, Pencil, Trash, FolderUp, RotateCw } from 'lucide-react';
import type { Tables } from '@/types_db';
import { ActionModal } from '../ActionModal/ActionModal';
import Button from '../Button';
import LoadingDots from '../LoadingDots';
import { useRouter } from 'next/navigation';
import Input from '../Input';
import { MoveModelModal } from '../MoveModelModal/MoveModelModal';
import { FORMAT } from '@/utils/constants';
import { analytics } from '@/lib/analytics';
import { Expand } from '../Expand/Expand';
import { retryTraining } from '@/utils/training/retry';
type Model = Tables<'models'>;

interface ModelMenuProps {
  model: Partial<Model>;
  platform: FORMAT;
  userId: string;
  toneId: number;
  isTraining: boolean;
  /**
   * True only when the linked training failed with an UNKNOWN error
   * (GPU crash / hang / heartbeat timeout) — i.e. `is_error && !error`.
   * Gates the "Retry" menu item. Known errors are user-fixable and
   * aren't retryable.
   */
  canRetry?: boolean;
  /** Training id to retry. Required for the Retry action to fire. */
  trainingId?: number;
}

export const ModelMenu = ({
  model,
  platform,
  userId,
  toneId,
  isTraining,
  canRetry = false,
  trainingId,
}: ModelMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [modelName, setModelName] = useState(model.name || '');
  const [inputLevel, setInputLevel] = useState(model.input_level || undefined);
  const [outputLevel, setOutputLevel] = useState(model.output_level || undefined);
  const menuRef = useRef(null);
  const router = useRouter();

  useEffect(() => {
    if (isOpen) setModelName(model.name!);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: { target: any }) => {
      // @ts-ignore
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const onDelete = async (id: number) => {
    setIsDeleting(true);
    const res = await fetch('/api/models/delete', {
      method: 'POST',
      body: JSON.stringify({ modelId: id }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (res.ok) {
      router.refresh();
    }
    setIsDeleting(false);
    analytics.capture('model.deleted');
  };

  const onEdit = async (
    id: number,
    name: string,
    inputLevel: number | undefined,
    outputLevel: number | undefined
  ) => {
    analytics.capture('model.edited');
    setIsEditing(true);
    const res = await fetch('/api/models/edit', {
      method: 'POST',
      body: JSON.stringify({ modelId: id, name, inputLevel, outputLevel }),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (res.ok) {
      router.refresh();
      setIsOpen(false);
    }
    setIsEditing(false);
  };

  const onRetry = async () => {
    if (!trainingId) return;
    analytics.capture('training.retried', { trainingId, from: 'model_menu' });
    // Close the menu immediately — the retry fires in the background
    // and the row goes back to in-progress on the next refresh.
    setIsOpen(false);
    const res = await retryTraining(trainingId);
    if (res.ok) {
      // SSR re-run re-enables live polling for the now-active row.
      router.refresh();
    }
  };

  return (
    <div className="relative" ref={menuRef}>
      <button onClick={() => setIsOpen(!isOpen)} className={'px-2 py-2'}>
        <Ellipsis size={20} />
      </button>

      <nav
        className={`${isOpen ? 'block' : 'hidden'} absolute z-50 right-0 top-10 bg-black text-white rounded-lg shadow-lg border border-zinc-500 border-opacity-50 min-w-[160px] bg-zinc-900`}
      >
        <div className="flex flex-col p-3">
          {canRetry && trainingId && (
            <button
              onClick={onRetry}
              className={`${s.link} w-full whitespace-nowrap gap-2 py-2 px-3 hover:bg-zinc-800 rounded-lg flex items-center`}
            >
              <RotateCw size={20} className="flex-shrink-0" />
              Retry
            </button>
          )}
          <ActionModal
            trigger={
              <span
                className={`${s.link} w-full whitespace-nowrap gap-2 py-2 px-3 hover:bg-zinc-800 rounded-lg`}
              >
                <Pencil size={20} className="flex-shrink-0" />
                Edit
              </span>
            }
            title={'Edit Model'}
            message={
              <span className="flex flex-col gap-6">
                <span className="flex flex-col gap-2">
                  <label className="text-sm text-zinc-400" htmlFor="modelName">
                    Model Name (Include Capture Details)*
                  </label>
                  <Input
                    id="modelName"
                    value={modelName}
                    onChange={setModelName}
                    placeholder="Fender Princeton Bass 5, Mid 5, Treble 5 etc."
                    style={{ background: 'rgb(24 24 27' }}
                  />
                </span>
                {/*
                  Calibration is editable for any NAM model — including
                  while a training is still in flight. The values flow
                  through `models.input_level` / `models.output_level`
                  and get picked up by `train-model`'s
                  `loadStampSources` right before the .nam upload, so
                  late edits still make it into the published file.
                  Post-training, the edit route re-stamps the existing
                  .nam in storage (see `api/models/edit/route.ts`).
                */}
                {platform === FORMAT.NAM && (
                  <Expand title="Calibration" defaultExpanded={false}>
                    <div className={'flex gap-4'}>
                      <div className={'inline-flex flex-1 flex-col gap-1 w-full'}>
                        <label className={'text-zinc-400 text-sm'}>Send Level (dBu)</label>
                        <Input
                          onChange={(e) => setInputLevel(e ? parseFloat(e) : undefined)}
                          defaultValue={inputLevel}
                          placeholder={'Optional'}
                          type={'number'}
                          min={-100}
                          max={100}
                        />
                      </div>
                      <div className={'inline-flex flex-1 flex-col gap-1 w-full'}>
                        <label className={'text-zinc-400 text-sm'}>Return Level (dBu)</label>
                        <Input
                          onChange={(e) => setOutputLevel(e ? parseFloat(e) : undefined)}
                          defaultValue={outputLevel}
                          placeholder={'Optional'}
                          type={'number'}
                          min={-100}
                          max={100}
                        />
                      </div>
                    </div>
                  </Expand>
                )}
              </span>
            }
            primaryAction={
              <Button
                className={`w-full text-white ${!modelName && 'cursor-not-allowed pointer-events-none opacity-50'}`}
                isPrimary={true}
                onClick={() => onEdit(model.id!, modelName, inputLevel, outputLevel)}
                variant={'slim'}
              >
                {isEditing && <LoadingDots />}
                Save
              </Button>
            }
            isCancelable={false}
          />
          <MoveModelModal
            trigger={
              <span
                className={`${s.link} w-full whitespace-nowrap gap-2 py-2 px-3 hover:bg-zinc-800 rounded-lg`}
              >
                <FolderUp size={20} className="flex-shrink-0" />
                Move
              </span>
            }
            platform={platform}
            modelId={model.id!}
            userId={userId}
            toneId={toneId}
          />
          <ActionModal
            trigger={
              <span
                className={`${s.link} w-full whitespace-nowrap gap-2 py-2 px-3 hover:bg-zinc-800 rounded-lg`}
              >
                <Trash size={20} className="flex-shrink-0" />
                Delete
              </span>
            }
            disabled={isTraining}
            title={'Delete Model'}
            message={`Are you sure you want to delete "${model.name}"? This action cannot be undone.`}
            primaryAction={
              <Button
                className={'w-full text-white'}
                style={{ background: 'red', color: 'white' }}
                onClick={() => onDelete(model.id!)}
                variant={'slim'}
              >
                {isDeleting && <LoadingDots />}
                Delete
              </Button>
            }
            cancelText={'Cancel'}
          />
        </div>
      </nav>
    </div>
  );
};
